import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import {
  getPeriodDateRange,
  getSummaryEarlyDays,
  getSummaryOnTimeDays,
  hoursStatusClass,
  hoursStatusLabel,
} from '../../utils/hrReportUtils';
import { fetchWithHrToken, HR_SESSION_ERROR, todayIsoLocal } from '../../utils/hrApiHelpers';
import HrEmployeeSearch from './HrEmployeeSearch';
import HrLinesModal from './HrLinesModal';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const SUMMARY_STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'present', label: 'Present' },
  { value: 'early', label: 'Early' },
  { value: 'normal', label: 'On time' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
];

function KpiCard({ label, value, accent, onClick, clickable }) {
  const Tag = clickable ? 'button' : 'article';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      className={`ea-hr-kpi ea-hr-kpi--${accent}${clickable ? ' ea-hr-kpi--clickable' : ''}`}
      onClick={onClick}
    >
      <p className="ea-hr-kpi__label">{label}</p>
      <p className="ea-hr-kpi__value">{value}</p>
    </Tag>
  );
}

function pickToday(report) {
  return report?.today || report?.today_kpis || report?.kpis_today || null;
}

function todayVal(today, ...keys) {
  if (!today) return '—';
  for (const key of keys) {
    if (today[key] != null) return today[key];
  }
  return 0;
}

export default function HrReportsDashboard({ embedded = false }) {
  const navigate = useNavigate();
  const { ensureHrToken, user } = useEmployeeAttendanceHrAuth();
  const { user: staffUser } = useEmployeeAttendanceAuth();
  const initialRange = getPeriodDateRange('monthly');

  const [periodType, setPeriodType] = useState('monthly');
  const [dateFrom, setDateFrom] = useState(initialRange.date_from);
  const [dateTo, setDateTo] = useState(initialRange.date_to);
  const [employeeId, setEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(null);
  const [activeEmployeeCount, setActiveEmployeeCount] = useState(null);
  const [inactiveEmployeeCount, setInactiveEmployeeCount] = useState(null);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [summaryStatusFilter, setSummaryStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState(null);
  const [linesModal, setLinesModal] = useState(null);

  const loadEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      const { data, error: fetchError } = await fetchWithHrToken(
        ensureHrToken,
        (token) => employeeAttendanceHrAPI.getPortalEmployees(token)
      );
      if (fetchError === 'no_token') {
        setEmployees([]);
        setTotalEmployeeCount(null);
        setActiveEmployeeCount(null);
        setInactiveEmployeeCount(null);
        setError(staffUser?.hr_api_error || HR_SESSION_ERROR);
        return;
      }
      if (fetchError) {
        setEmployees([]);
        setTotalEmployeeCount(null);
        setActiveEmployeeCount(null);
        setInactiveEmployeeCount(null);
        setError(fetchError);
        return;
      }
      const list = (data.employees || []).map((e) => ({
        ...e,
        department: e.department || e.department_name,
      }));
      setEmployees(list);
      setTotalEmployeeCount(
        data.total_employee_count ??
          data.total_employees ??
          data.all_employee_count ??
          data.count ??
          list.length
      );
      setActiveEmployeeCount(data.active_employee_count ?? data.active_employees ?? null);
      setInactiveEmployeeCount(
        data.inactive_employee_count ?? data.inactive_employees ?? null
      );
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load employees');
      setEmployees([]);
      setTotalEmployeeCount(null);
      setActiveEmployeeCount(null);
      setInactiveEmployeeCount(null);
    } finally {
      setLoadingEmployees(false);
    }
  }, [ensureHrToken, staffUser?.hr_api_error]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        period_type: periodType,
        date_from: dateFrom,
        date_to: dateTo,
      };
      if (employeeId) {
        params.employee_id = Number(employeeId);
      }
      const { data, error: fetchError } = await fetchWithHrToken(
        ensureHrToken,
        (token) => employeeAttendanceHrAPI.getReport(params, token)
      );
      if (fetchError === 'no_token') {
        setError(staffUser?.hr_api_error || HR_SESSION_ERROR);
        setReport(null);
        return;
      }
      if (fetchError) {
        setError(fetchError);
        setReport(null);
        return;
      }
      setReport(data.report || null);
    } catch (err) {
      setError(err.message || 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [ensureHrToken, periodType, dateFrom, dateTo, employeeId, staffUser?.hr_api_error]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handlePeriodChange = (next) => {
    setPeriodType(next);
    const range = getPeriodDateRange(next);
    setDateFrom(range.date_from);
    setDateTo(range.date_to);
  };

  const summaryRows = report?.summary || [];
  const hoursRows = report?.hours || [];
  const today = pickToday(report);
  const todayIso = todayIsoLocal();

  const portalEmployeeCount =
    report?.portal_employee_count ?? user?.portal_employee_count ?? '—';

  const allEmployeesCount =
    report?.total_employee_count ??
    report?.total_employees ??
    totalEmployeeCount ??
    (loadingEmployees ? '—' : 0);

  const activeEmployeesCount =
    report?.active_employee_count ??
    report?.active_employees ??
    activeEmployeeCount ??
    (loadingEmployees ? '—' : 0);

  const inactiveEmployeesCount =
    report?.inactive_employee_count ??
    report?.inactive_employees ??
    inactiveEmployeeCount ??
    (typeof allEmployeesCount === 'number' &&
    typeof activeEmployeesCount === 'number'
      ? allEmployeesCount - activeEmployeesCount
      : loadingEmployees
        ? '—'
        : 0);

  const openLines = (row, statusFilter = 'all') => {
    setLinesModal({
      employeeId: Number(row.employee_id),
      employeeName: row.employee_name,
      statusFilter,
      summary: row,
    });
  };

  const openTodayLines = (statusFilter = 'all') => {
    const params = new URLSearchParams({
      date_from: todayIso,
      date_to: todayIso,
      status: statusFilter,
    });
    navigate(`/bu-dashboard/hr/attendance?${params.toString()}`);
  };

  const getClickableRowProps = (row, statusFilter = 'all') => ({
    className: 'ea-hr-table__row--clickable',
    onClick: () => openLines(row, statusFilter),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openLines(row, statusFilter);
      }
    },
    tabIndex: 0,
    role: 'button',
    'aria-label': `View attendance lines for ${row.employee_name}`,
  });

  const showPresentCol = !summaryStatusFilter || summaryStatusFilter === 'present';
  const showEarlyCol = !summaryStatusFilter || summaryStatusFilter === 'early';
  const showOnTimeCol = !summaryStatusFilter || summaryStatusFilter === 'normal';
  const showAbsentCol = !summaryStatusFilter || summaryStatusFilter === 'absent';
  const showLateCol = !summaryStatusFilter || summaryStatusFilter === 'late';
  const summaryColCount =
    2 +
    (showPresentCol ? 1 : 0) +
    (showEarlyCol ? 1 : 0) +
    (showOnTimeCol ? 1 : 0) +
    (showAbsentCol ? 1 : 0) +
    (showLateCol ? 1 : 0);

  const workingDays =
    report?.period_calendar_day_count ?? report?.working_days ?? report?.totals?.total_days;

  return (
    <div className={`ea-hr-dash${embedded ? ' ea-hr-dash--embedded' : ''}`}>
      {!embedded && (
        <header className="ea-dash-header">
          <div>
            <h1 className="ea-page-title">Employee Attendance Report</h1>
            <p className="ea-page-sub">
              Fridays excluded · Expected 10h per working day
              {workingDays != null ? ` · ${workingDays} working days in range` : ''}
            </p>
          </div>
        </header>
      )}

      <section className="ea-hr-kpis ea-hr-kpis--quad" aria-label="Employee totals">
        <KpiCard label="Total employees" value={allEmployeesCount} accent="slate" />
        <KpiCard label="Portal employees" value={portalEmployeeCount} accent="blue" />
        <KpiCard label="Active employees" value={activeEmployeesCount} accent="green" />
        <KpiCard label="Inactive employees" value={inactiveEmployeesCount} accent="amber" />
      </section>

      {today && (
        <section className="ea-hr-kpis ea-hr-kpis--today" aria-label="Today KPIs">
          <KpiCard
            label="Checked in"
            value={todayVal(today, 'checked_in', 'checked_in_count')}
            accent="blue"
            clickable
            onClick={() => openTodayLines('all')}
          />
          <KpiCard
            label="Early"
            value={todayVal(today, 'early', 'early_count')}
            accent="green"
            clickable
            onClick={() => openTodayLines('early')}
          />
          <KpiCard
            label="On time"
            value={todayVal(today, 'ontime', 'on_time', 'normal', 'ontime_count')}
            accent="violet"
            clickable
            onClick={() => openTodayLines('normal')}
          />
          <KpiCard
            label="Late"
            value={todayVal(today, 'late', 'late_count')}
            accent="amber"
            clickable
            onClick={() => openTodayLines('late')}
          />
          <KpiCard
            label="Not checked in"
            value={todayVal(today, 'not_checked_in', 'not_checked_in_count')}
            accent="slate"
            clickable
            onClick={() => openTodayLines('all')}
          />
          <KpiCard
            label="Checked out"
            value={todayVal(today, 'checked_out', 'checked_out_count')}
            accent="green"
            clickable
            onClick={() => openTodayLines('all')}
          />
        </section>
      )}

      <div className="ea-hr-filters">
        <div className="ea-hr-filters__row">
          <label>
            <span className="ea-field-label">Period</span>
            <select
              className="ea-field-input"
              value={periodType}
              onChange={(e) => handlePeriodChange(e.target.value)}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="ea-field-label">From</span>
            <input
              type="date"
              className="ea-field-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label>
            <span className="ea-field-label">To</span>
            <input
              type="date"
              className="ea-field-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <label className="ea-hr-filters__employee">
            <span className="ea-field-label">Employee</span>
            <HrEmployeeSearch
              employees={employees}
              value={employeeId}
              onChange={setEmployeeId}
              loading={loadingEmployees}
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      {loading && !report ? (
        <div className="ea-dash-loading">
          <div className="ea-dash-loading__spinner" />
          <p>Loading attendance report…</p>
        </div>
      ) : (
        <>
          <div className="ea-hr-tabs" role="tablist" aria-label="Report views">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'summary'}
              className={`ea-hr-tabs__btn ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              Summary
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'hours'}
              className={`ea-hr-tabs__btn ${activeTab === 'hours' ? 'active' : ''}`}
              onClick={() => setActiveTab('hours')}
            >
              Worked vs expected hours
            </button>
          </div>

          {activeTab === 'summary' && (
            <section className="ea-hr-card">
              <h2 className="ea-hr-card__title">Attendance summary</h2>
              <p className="ea-hr-card__sub">
                Click a row to view daily attendance lines
              </p>
              <div
                className="ea-hr-summary-filters"
                role="group"
                aria-label="Attendance status filter"
              >
                <span className="ea-hr-summary-filters__label">Status</span>
                <div className="ea-hr-summary-filters__toggle">
                  {SUMMARY_STATUS_FILTERS.map(({ value, label }) => (
                    <button
                      key={value || 'all'}
                      type="button"
                      className={`ea-hr-summary-filters__btn ${
                        summaryStatusFilter === value ? 'active' : ''
                      }`}
                      onClick={() => setSummaryStatusFilter(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ea-hr-table-wrap">
                <table className="ea-hr-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Total days</th>
                      {showPresentCol && <th>Present</th>}
                      {showEarlyCol && <th>Early</th>}
                      {showOnTimeCol && <th>On time</th>}
                      {showAbsentCol && <th>Absent</th>}
                      {showLateCol && <th>Late</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.length === 0 ? (
                      <tr>
                        <td colSpan={summaryColCount} className="ea-hr-table__empty">
                          No employees in report. Grant portal access from Employees.
                        </td>
                      </tr>
                    ) : (
                      summaryRows.map((row) => (
                        <tr
                          key={row.employee_id}
                          {...getClickableRowProps(row, 'all')}
                        >
                          <td className="ea-hr-table__name">{row.employee_name}</td>
                          <td>{row.total_days}</td>
                          {showPresentCol && <td>{row.present_days}</td>}
                          {showEarlyCol && <td>{getSummaryEarlyDays(row)}</td>}
                          {showOnTimeCol && <td>{getSummaryOnTimeDays(row)}</td>}
                          {showAbsentCol && <td>{row.absent_days}</td>}
                          {showLateCol && <td>{row.late_days}</td>}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === 'hours' && (
            <section className="ea-hr-card">
              <h2 className="ea-hr-card__title">Worked vs expected hours</h2>
              <p className="ea-hr-card__sub">Expected: 10h per counted day in range</p>
              <div className="ea-hr-table-wrap">
                <table className="ea-hr-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Expected (h)</th>
                      <th>Worked (h)</th>
                      <th>Difference</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoursRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="ea-hr-table__empty">
                          No hours data for this period.
                        </td>
                      </tr>
                    ) : (
                      hoursRows.map((row) => (
                        <tr key={row.employee_id} {...getClickableRowProps(row)}>
                          <td className="ea-hr-table__name">{row.employee_name}</td>
                          <td>{Number(row.expected_hours).toFixed(1)}</td>
                          <td>{Number(row.worked_hours).toFixed(1)}</td>
                          <td>{row.difference_display ?? Number(row.hours_difference).toFixed(2)}</td>
                          <td>
                            <span
                              className={`ea-hr-status ${hoursStatusClass(row.hours_status)}`}
                            >
                              {hoursStatusLabel(row.hours_status)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {linesModal && (
        <HrLinesModal
          token={localStorage.getItem('ea_hr_token')}
          employeeId={linesModal.employeeId}
          employeeName={linesModal.employeeName}
          dateFrom={dateFrom}
          dateTo={dateTo}
          statusFilter={linesModal.statusFilter}
          summary={linesModal.summary}
          onClose={() => setLinesModal(null)}
        />
      )}
    </div>
  );
}
